import { Stack } from '@mui/material';
import { LinkedinTypes, ProductGroups, ZoomLevels } from 'enums';
import _ from 'lodash';
import {
  FileUpload,
  Funnel,
  FunnelContact,
  FunnelFunctionFlow,
  RecruiterLocationSearch,
  RegularConstraint,
  Search,
} from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import React from 'react';
import { Node } from 'reactflow';
import useCampaignFlowStore from 'stores/campaign-flow-store';
import useCampaignNodeStore from 'stores/campaign-node-store';
import { makeStyles } from 'tss-react/mui';
import { Blacklist_New } from '../company-blacklists/company-blacklist.types';
import {
  AutoReplyCase,
  BlacklistScopes,
  CampaignBuilderSteps,
  CampaignPipelineSteps,
  CampaignTestingState,
  ContactFilter,
  ContactSearchMethods,
  ContactToAdd,
  LinkedinSearch,
  LinkedinSearchResult,
  Offer,
  QualificationPromptPlaceholders,
  Qualifications,
  SearchObjective,
  SearchToAdd,
  SingleSearch,
} from './campaign-builder.types';
import CampaignBuilderHeader from './campaign-builder-header';
import CampaignBuildingPanel from './campaign-building-panel/campaign-building-panel';
import CampaignFlow from './campaign-flow/campaign-flow';
import { Shadows } from 'theme';

interface Props {
  productGroup: ProductGroups;
  campaign: Funnel;
  hasJudgingSettings: boolean;

  fullLinkedinAccount: LinkedinAccount | undefined;
  linkedinAccountLoading: boolean;
  linkedinAccountUpdating: boolean;

  fullSeparateLinkedinAccount: LinkedinAccount | undefined;
  updateCampaignLoading: boolean;

  autoSyncLoading: boolean;
  autoSyncProcessingDialogOpen: boolean;
  handleOpenAutoSyncProcessingDialog: () => void;

  expanded: boolean;
  activeCampaignStepIndex: number;

  perplexityUrl: string;
  perplexityLoading: boolean;
  perplexityError: {
    noJobAd: boolean;
    noJobTitle: boolean;
    noProblems: boolean;
    noFeatures: boolean;
    noBenefits: boolean;
    noImpact: boolean;
  };
  perplexityStatusMessage: string;
  offer: Offer;

  activeCampaignPipelineStep: CampaignPipelineSteps;
  contactSearchMethod: ContactSearchMethods | '';

  linkedinProduct: LinkedinTypes | '';
  paiSearchLanguage: string[];
  includeOwnContacts: boolean;
  regularLocationConstraints: RegularConstraint;
  recruiterLocationConstraints: RecruiterLocationSearch;
  linkedinSearch: LinkedinSearch;

  selectedContactSearchObjective: SearchObjective | null;
  searchToAdd: SearchToAdd | null;
  separateLinkedinAccount: string;
  addSearchesLoading: boolean;

  contactsToAdd: ContactToAdd[];
  addContactsLoading: boolean;

  searches: Search[];
  searchesLoading: boolean;

  linkedinAccounts: LinkedinAccount[];
  activeBlacklistScope: BlacklistScopes;
  globalBlacklists: Blacklist_New[];
  localBlacklists: Blacklist_New[];
  globalBlacklistsLoading: boolean;
  localBlacklistsLoading: boolean;
  removeBlacklistItemsLoading: boolean;

  contactsPage: number;
  contactsSearchParam: string;
  contactsPerPage: number;
  totalContacts: number;
  contactFilter: ContactFilter;
  activeQualifications: Qualifications[];
  abortRequested: boolean;
  abortLoading: boolean;
  deleteMultipleContacts: boolean;
  contactsToDelete: FunnelContact[];

  aiJudgementProcessing: boolean;
  profileAnalysisProcessing: boolean;
  contacts: FunnelContact[];
  contactsLoading: boolean;

  campaignTesting: CampaignTestingState;

  finalContacts: FunnelContact[];
  finalContactsPage: number;
  finalContactsSearchParam: string;
  finalContactsPerPage: number;
  totalFinalContacts: number;
  finalContactsLoading: boolean;
  finalContactsProcessing: boolean;
  exportFinalContactsLoading: boolean;

  startCampaignLoading: boolean;
  stopCampaignLoading: boolean;

  increaseCampaignStepIndex(): void;

  decreaseCampaignStepIndex(): void;

  selectCampaignStep(idx: number): () => void;

  handlePerplexityUrlChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleOfferInputChange(type: keyof Offer): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleOfferToggleChange(type: keyof Offer): (inUse: boolean) => void;

  handlePerplexityPaste(event: React.ClipboardEvent): void;

  handlePerplexityKeyDown(event: React.KeyboardEvent): void;

  handlePerplexityRefresh(): void;

  handleActiveCampaignPipelineStepChange(newStep: CampaignPipelineSteps): () => void;

  handleContactSearchMethodChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleMoveToNewSearch(): void;

  handleLinkedinProductChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handlePaiSearchLanguageChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleIncludeOwnContactsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleLocationFieldChange(event: React.ChangeEvent<HTMLInputElement>): void;

  searchKeyUp(): void;

  searchKeyDown(): void;

  handleMultiSelectLocationsChange(value: string): () => void;

  handleMultiSelectLocationsRemove(value: string): () => void;

  handleSelectRecruiterLocationConstraint(searchType: 'geo' | 'zip'): (value: string) => () => void;

  handleRemoveRecruiterLocationConstraint(searchType: 'geo' | 'zip', urn: string): () => void;

  handleRecruiterLocationConstraintQueryChange(searchType: 'geo' | 'zip'): (event: React.ChangeEvent<HTMLInputElement>) => void;

  searchRecruiterLocationKeyUp(searchType: 'geo' | 'zip'): () => void;

  searchRecruiterLocationKeyDown(): void;

  handleRecruiterLocationAreaChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleResetLinkedinSearch(): void;

  handleResetLocationSettings(): void;

  handleStartLinkedinSearch(): void;

  handleOpenRefineLinkedinSearchDialog(linkedinSearch: LinkedinSearchResult, idx: number): () => void;

  handleOpenCreateNewSearchDialog(): void;

  onOpenFeedbackDialog?(): void;

  handleSelectContactSearchObjective(newObjective: SearchObjective): () => void;

  handleOwnSearchFieldChange(fieldType: keyof SingleSearch): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handlePeopleTypeChange?: (type: 'includeReactions' | 'includeComments' | 'includeAuthor') => (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleProfileDegreeChange?: (value: 1 | 2 | 3) => (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleSeparateLinkedinAccountChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleAddOwnLinkedinSearch(): void;

  handleAddOwnLinkedinSearchWithSkipQualification(): void;

  handleJobTitlesChange?: (titles: string[]) => void;

  onFileDrop(files: any): void;

  handleFileChange(event: React.ChangeEvent<HTMLInputElement>): void;

  exportCampaignContacts(): void;

  handleChangeFinalContactsPage(event: any, value: number): void;

  handleFinalContactsSearchChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClearFinalContactsSearchField(): void;

  handleDeleteContacts(): void;

  handleAddContactsFromFile(): void;

  handleOpenRenameContactSearchDialog(search: Search): () => void;

  handleOpenDeleteContactsSearchDialog(search: Search): () => void;

  handleOpenDeleteContactsFileDialog(file: FileUpload): () => void;

  handleOpenAddSingleContactDialog(): void;

  handleAddMultipleContacts(): void;

  handleOpenDeleteContactPermanentlyDialog(contact: FunnelContact): () => void;

  handleOpenChangeAiJudgementDialog(contact: FunnelContact): () => void;

  handleOpenLoadContactsNumberDialog(search: Search): () => void;

  handleOpenEditSearchLinkedinAccountDialog(search: Search): () => void;

  handleBlacklistScopeChange(newScope: BlacklistScopes): () => void;

  handleSelectedBlacklistsChange(blacklist: Blacklist_New): () => void;

  handleOpenAddLocalBlacklistDialog(): void;

  handleOpenEditBlacklistItemsDialog(blacklist: Blacklist_New): () => void;

  handleOpenEditBlacklistNameDialog(blacklist: Blacklist_New): () => void;

  handleOpenDeleteBlacklistDialog(blacklist: Blacklist_New): () => void;

  handleRemoveSingleBlacklistItem(blacklist: Blacklist_New): (identifier: string) => () => void;

  handleSingleContactIsActiveChange(contact: FunnelContact): () => void;

  handleMultiContactIsActiveChange(): void;

  handleChangeContactsPage(event: any, value: number): void;

  handleContactsSearchChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClearContactsSearchField(): void;

  handleOpenContactDrawer(linkedInURL: string | undefined, profileId: string | undefined, campaignId: string | undefined): () => void;

  handleOpenEnrichContactsDialog(): void;

  handleSelectedFunnelFunctionsLibraryChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSelectNodeFromList(node: Node): () => void;

  handleUnselectNodeFromList(): void;

  handleUpdateCampaignReactFlow(flow: FunnelFunctionFlow, signal: AbortSignal): void;

  handleOpenFlowCreationDialog(): void;

  handleFlowBuilderApplied(campaign: Funnel): void;

  handleOpenChangeCommunicationStyleWarningDialog(): void;

  handleIncludeEmojiChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSwissSChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleLanguageChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSecondaryLanguageChange(event: React.ChangeEvent<HTMLInputElement>): void;

  onIsActiveChange(event: React.ChangeEvent<HTMLInputElement>): void;

  onGoalChange(event: React.ChangeEvent<HTMLInputElement>): void;

  onCaseHandlingUpdate(newCaseHandling: AutoReplyCase[]): void;

  handleUpdateAutoReply(): void;

  handleMessageApprovalChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleRecontactCooldownChange(event: React.ChangeEvent<HTMLInputElement>): void;

  isLinkedinAccountMissing(): boolean;

  handleSelectedLinkedinUserChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSelectedTestContactChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleNextContact(): void;

  handlePrevContact(): void;

  handleNextMessage(): void;

  handlePrevMessage(): void;

  handleTestCampaign(): void;

  handleStartCampaign(): void;

  handleStopCampaign(): void;

  toggleExpanded(): void;

  handleOpenFaqDialog(): void;

  /** Recruiting-Entwurf aus dem Assistenten: zurück in den Assistenten „Neue Kampagne“. */
  handleOpenWizard?: () => void;

  handleUpdateCampaign(value: Partial<Funnel>): void;

  handleOpenStopRunningCampaignDialog(campaignBuilderStep: CampaignBuilderSteps): () => void;

  contactQualification: {
    loading: boolean;
    promptPlaceholders: QualificationPromptPlaceholders;
    selectedOptions: string[];
    rejudgeExistingContacts: boolean;
    prompt: string;
    hasOldKeys: boolean;
    isJudging: boolean;
  };

  handleAiQualifierDetailsChange(type: keyof QualificationPromptPlaceholders): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleAiQualifierPromptChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleAiQualifierSelectedOptionChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleRejudgeExistingContactsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleIsJudgingChange(event: React.ChangeEvent<HTMLInputElement>): void;

  updateQualifyingContactsRecruiting(): void;

  updateQualifyingContactsLead(): void;

  handleRemoveContactFromBlacklist(linkedInURL: string): () => void;

  handleRemoveContactFromSuspended(linkedInURL: string): () => void;

  handleOpenUpdateSexDialog(contact: FunnelContact): () => void;

  handleRecoverContactFromDeleted(linkedInURL: string): () => void;

  handleContactFilterChange(event: React.ChangeEvent<HTMLInputElement>): void;

  applySidebarFilter(filter: ContactFilter): () => void;

  handleActiveQualificationsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleAbortProcessing(): void;

  toggleDeleteMultipleContacts(): void;

  handleMultipleContactsToDeleteChange(event: React.MouseEvent<HTMLInputElement>): void;

  handleContactsToDeleteChange(contact: FunnelContact): (event: React.MouseEvent<HTMLInputElement>) => void;

  handleOpenDeleteMultipleContactsPermanentlyDialog(): void;
}

const CampaignBuilder: React.FC<Props> = ({
  productGroup,
  campaign,
  hasJudgingSettings,
  fullLinkedinAccount,
  linkedinAccountLoading,
  linkedinAccountUpdating,
  fullSeparateLinkedinAccount,
  updateCampaignLoading,
  autoSyncLoading,
  autoSyncProcessingDialogOpen,
  handleOpenAutoSyncProcessingDialog,
  expanded,
  activeCampaignStepIndex,
  perplexityUrl,
  perplexityLoading,
  perplexityError,
  perplexityStatusMessage,
  offer,
  activeCampaignPipelineStep,
  contactSearchMethod,
  linkedinProduct,
  paiSearchLanguage,
  includeOwnContacts,
  regularLocationConstraints,
  recruiterLocationConstraints,
  linkedinSearch,
  selectedContactSearchObjective,
  searchToAdd,
  separateLinkedinAccount,
  addSearchesLoading,
  contactsToAdd,
  addContactsLoading,
  searches,
  searchesLoading,
  linkedinAccounts,
  activeBlacklistScope,
  globalBlacklists,
  localBlacklists,
  globalBlacklistsLoading,
  localBlacklistsLoading,
  removeBlacklistItemsLoading,
  contactsPage,
  contactsSearchParam,
  contactsPerPage,
  totalContacts,
  contactFilter,
  activeQualifications,
  abortRequested,
  abortLoading,
  deleteMultipleContacts,
  contactsToDelete,
  aiJudgementProcessing,
  profileAnalysisProcessing,
  contacts,
  contactsLoading,
  campaignTesting,
  finalContacts,
  finalContactsPage,
  finalContactsSearchParam,
  finalContactsPerPage,
  totalFinalContacts,
  finalContactsLoading,
  finalContactsProcessing,
  exportFinalContactsLoading,
  startCampaignLoading,
  stopCampaignLoading,
  increaseCampaignStepIndex,
  decreaseCampaignStepIndex,
  selectCampaignStep,
  handlePerplexityUrlChange,
  handleOfferInputChange,
  handleOfferToggleChange,
  handlePerplexityPaste,
  handlePerplexityKeyDown,
  handlePerplexityRefresh,
  handleActiveCampaignPipelineStepChange,
  handleContactSearchMethodChange,
  handleMoveToNewSearch,
  handleLinkedinProductChange,
  handlePaiSearchLanguageChange,
  handleIncludeOwnContactsChange,
  handleLocationFieldChange,
  searchKeyUp,
  searchKeyDown,
  handleMultiSelectLocationsChange,
  handleMultiSelectLocationsRemove,
  handleSelectRecruiterLocationConstraint,
  handleRemoveRecruiterLocationConstraint,
  handleRecruiterLocationConstraintQueryChange,
  searchRecruiterLocationKeyUp,
  searchRecruiterLocationKeyDown,
  handleRecruiterLocationAreaChange,
  handleResetLinkedinSearch,
  handleResetLocationSettings,
  handleStartLinkedinSearch,
  handleOpenRefineLinkedinSearchDialog,
  handleOpenCreateNewSearchDialog,
  onOpenFeedbackDialog,
  handleSelectContactSearchObjective,
  handleOwnSearchFieldChange,
  handlePeopleTypeChange,
  handleProfileDegreeChange,
  handleSeparateLinkedinAccountChange,
  handleAddOwnLinkedinSearch,
  handleAddOwnLinkedinSearchWithSkipQualification,
  handleJobTitlesChange,
  onFileDrop,
  handleFileChange,
  exportCampaignContacts,
  handleChangeFinalContactsPage,
  handleFinalContactsSearchChange,
  handleClearFinalContactsSearchField,
  handleDeleteContacts,
  handleAddContactsFromFile,
  handleOpenRenameContactSearchDialog,
  handleOpenDeleteContactsSearchDialog,
  handleOpenDeleteContactsFileDialog,
  handleOpenAddSingleContactDialog,
  handleAddMultipleContacts,
  handleOpenDeleteContactPermanentlyDialog,
  handleOpenChangeAiJudgementDialog,
  handleOpenLoadContactsNumberDialog,
  handleOpenEditSearchLinkedinAccountDialog,
  handleBlacklistScopeChange,
  handleSelectedBlacklistsChange,
  handleOpenAddLocalBlacklistDialog,
  handleOpenEditBlacklistItemsDialog,
  handleOpenEditBlacklistNameDialog,
  handleOpenDeleteBlacklistDialog,
  handleRemoveSingleBlacklistItem,
  handleSingleContactIsActiveChange,
  handleMultiContactIsActiveChange,
  handleChangeContactsPage,
  handleContactsSearchChange,
  handleClearContactsSearchField,
  handleOpenContactDrawer,
  handleOpenEnrichContactsDialog,
  handleSelectedFunnelFunctionsLibraryChange,
  handleSelectNodeFromList,
  handleUnselectNodeFromList,
  handleUpdateCampaignReactFlow,
  handleOpenFlowCreationDialog,
  handleFlowBuilderApplied,
  handleOpenChangeCommunicationStyleWarningDialog,
  handleIncludeEmojiChange,
  handleSwissSChange,
  handleLanguageChange,
  handleSecondaryLanguageChange,
  onIsActiveChange,
  onGoalChange,
  onCaseHandlingUpdate,
  handleUpdateAutoReply,
  handleMessageApprovalChange,
  handleRecontactCooldownChange,
  isLinkedinAccountMissing,
  handleSelectedLinkedinUserChange,
  handleSelectedTestContactChange,
  handleNextContact,
  handlePrevContact,
  handleNextMessage,
  handlePrevMessage,
  handleTestCampaign,
  handleStartCampaign,
  handleStopCampaign,
  toggleExpanded,
  handleOpenFaqDialog,
  handleOpenWizard,
  handleUpdateCampaign,
  handleOpenStopRunningCampaignDialog,
  contactQualification,
  handleAiQualifierDetailsChange,
  handleAiQualifierPromptChange,
  handleAiQualifierSelectedOptionChange,
  handleRejudgeExistingContactsChange,
  handleIsJudgingChange,
  updateQualifyingContactsRecruiting,
  updateQualifyingContactsLead,
  handleRemoveContactFromBlacklist,
  handleRemoveContactFromSuspended,
  handleOpenUpdateSexDialog,
  handleRecoverContactFromDeleted,
  handleContactFilterChange,
  applySidebarFilter,
  handleActiveQualificationsChange,
  handleAbortProcessing,
  toggleDeleteMultipleContacts,
  handleMultipleContactsToDeleteChange,
  handleContactsToDeleteChange,
  handleOpenDeleteMultipleContactsPermanentlyDialog,
}) => {
  const { classes } = useStyles();

  const isPromptChanging = useCampaignNodeStore((state) => state.isPromptChanging);
  const isFlowChanging = useCampaignFlowStore((state) => state.isFlowChanging);

  const campaignStepsArray = _.map(CampaignBuilderSteps, (step) => step);

  return (
    <Stack className={classes.campaignBuilder}>
      <CampaignBuilderHeader
        productGroup={productGroup}
        campaign={campaign}
        isUpdating={updateCampaignLoading || linkedinAccountUpdating}
        isFlowChanging={isFlowChanging || isPromptChanging}
        activeCampaignStepIndex={activeCampaignStepIndex}
        campaignStepsArray={campaignStepsArray}
        offer={offer}
        activeCampaignPipelineStep={activeCampaignPipelineStep}
        contactSearchMethod={contactSearchMethod}
        linkedinAccountLoading={linkedinAccountLoading}
        autoSyncLoading={autoSyncLoading}
        autoSyncProcessingDialogOpen={autoSyncProcessingDialogOpen}
        startCampaignLoading={startCampaignLoading}
        stopCampaignLoading={stopCampaignLoading}
        increaseCampaignStepIndex={increaseCampaignStepIndex}
        decreaseCampaignStepIndex={decreaseCampaignStepIndex}
        selectCampaignStep={selectCampaignStep}
        handleOpenStopRunningCampaignDialog={handleOpenStopRunningCampaignDialog}
        handleOpenAutoSyncProcessingDialog={handleOpenAutoSyncProcessingDialog}
        handleOpenFaqDialog={handleOpenFaqDialog}
        handleOpenWizard={handleOpenWizard}
        handleStartCampaign={handleStartCampaign}
        handleStopCampaign={handleStopCampaign}
      />
      <Stack className={classes.builderBody} direction={'row'}>
        <CampaignFlow
          expanded={expanded}
          selectedFunnelFunctionsLibrary={campaign?.funnelFunctionsLibrary || ''}
          productGroup={productGroup}
          campaign={campaign}
          linkedinAccount={fullLinkedinAccount}
          isUpdating={updateCampaignLoading || linkedinAccountUpdating}
          handleSelectedFunnelFunctionsLibraryChange={handleSelectedFunnelFunctionsLibraryChange}
          handleUpdateCampaignReactFlow={handleUpdateCampaignReactFlow}
        />
        <CampaignBuildingPanel
          productGroup={productGroup}
          campaign={campaign}
          hasJudgingSettings={hasJudgingSettings}
          fullLinkedinAccount={fullLinkedinAccount}
          linkedinAccountLoading={linkedinAccountLoading}
          fullSeparateLinkedinAccount={fullSeparateLinkedinAccount}
          autoSyncLoading={autoSyncLoading}
          isFlowChanging={isFlowChanging || isPromptChanging}
          isUpdating={updateCampaignLoading || linkedinAccountUpdating}
          expanded={expanded}
          activeCampaignStepIndex={activeCampaignStepIndex}
          perplexityUrl={perplexityUrl}
          perplexityLoading={perplexityLoading}
          perplexityError={perplexityError}
          perplexityStatusMessage={perplexityStatusMessage}
          offer={offer}
          activeCampaignPipelineStep={activeCampaignPipelineStep}
          contactSearchMethod={contactSearchMethod}
          linkedinProduct={linkedinProduct}
          paiSearchLanguage={paiSearchLanguage}
        includeOwnContacts={includeOwnContacts}
          regularLocationConstraints={regularLocationConstraints}
          recruiterLocationConstraints={recruiterLocationConstraints}
          linkedinSearch={linkedinSearch}
          selectedContactSearchObjective={selectedContactSearchObjective}
          searchToAdd={searchToAdd}
          addContactsLoading={addContactsLoading}
          searches={searches}
          searchesLoading={searchesLoading}
          linkedinAccounts={linkedinAccounts}
          separateLinkedinAccount={separateLinkedinAccount}
          addSearchesLoading={addSearchesLoading}
          contactsToAdd={contactsToAdd}
          activeBlacklistScope={activeBlacklistScope}
          globalBlacklists={globalBlacklists}
          localBlacklists={localBlacklists}
          globalBlacklistsLoading={globalBlacklistsLoading}
          localBlacklistsLoading={localBlacklistsLoading}
          removeBlacklistItemsLoading={removeBlacklistItemsLoading}
          contactsPage={contactsPage}
          contactsSearchParam={contactsSearchParam}
          contactsPerPage={contactsPerPage}
          totalContacts={totalContacts}
          contactFilter={contactFilter}
          activeQualifications={activeQualifications}
          abortRequested={abortRequested}
          abortLoading={abortLoading}
          deleteMultipleContacts={deleteMultipleContacts}
          contactsToDelete={contactsToDelete}
          aiJudgementProcessing={aiJudgementProcessing}
          profileAnalysisProcessing={profileAnalysisProcessing}
          contacts={contacts}
          contactsLoading={contactsLoading}
          campaignTesting={campaignTesting}
          finalContacts={finalContacts}
          finalContactsPage={finalContactsPage}
          finalContactsSearchParam={finalContactsSearchParam}
          finalContactsPerPage={finalContactsPerPage}
          totalFinalContacts={totalFinalContacts}
          finalContactsLoading={finalContactsLoading}
          finalContactsProcessing={finalContactsProcessing}
          exportFinalContactsLoading={exportFinalContactsLoading}
          selectCampaignStep={selectCampaignStep}
          handlePerplexityUrlChange={handlePerplexityUrlChange}
          handleOfferInputChange={handleOfferInputChange}
          handleOfferToggleChange={handleOfferToggleChange}
          handlePerplexityPaste={handlePerplexityPaste}
          handlePerplexityKeyDown={handlePerplexityKeyDown}
          handlePerplexityRefresh={handlePerplexityRefresh}
          handleActiveCampaignPipelineStepChange={handleActiveCampaignPipelineStepChange}
          handleContactSearchMethodChange={handleContactSearchMethodChange}
          handleMoveToNewSearch={handleMoveToNewSearch}
          handleLinkedinProductChange={handleLinkedinProductChange}
          handlePaiSearchLanguageChange={handlePaiSearchLanguageChange}
        handleIncludeOwnContactsChange={handleIncludeOwnContactsChange}
          handleLocationFieldChange={handleLocationFieldChange}
          searchKeyUp={searchKeyUp}
          searchKeyDown={searchKeyDown}
          handleMultiSelectLocationsChange={handleMultiSelectLocationsChange}
          handleMultiSelectLocationsRemove={handleMultiSelectLocationsRemove}
          handleSelectRecruiterLocationConstraint={handleSelectRecruiterLocationConstraint}
          handleRemoveRecruiterLocationConstraint={handleRemoveRecruiterLocationConstraint}
          handleRecruiterLocationConstraintQueryChange={handleRecruiterLocationConstraintQueryChange}
          searchRecruiterLocationKeyUp={searchRecruiterLocationKeyUp}
          searchRecruiterLocationKeyDown={searchRecruiterLocationKeyDown}
          handleRecruiterLocationAreaChange={handleRecruiterLocationAreaChange}
          handleResetLinkedinSearch={handleResetLinkedinSearch}
          handleResetLocationSettings={handleResetLocationSettings}
          handleStartLinkedinSearch={handleStartLinkedinSearch}
          handleOpenRefineLinkedinSearchDialog={handleOpenRefineLinkedinSearchDialog}
          handleOpenCreateNewSearchDialog={handleOpenCreateNewSearchDialog}
          onOpenFeedbackDialog={onOpenFeedbackDialog}
          handleSelectContactSearchObjective={handleSelectContactSearchObjective}
          handleOwnSearchFieldChange={handleOwnSearchFieldChange}
          handlePeopleTypeChange={handlePeopleTypeChange}
          handleProfileDegreeChange={handleProfileDegreeChange}
          handleSeparateLinkedinAccountChange={handleSeparateLinkedinAccountChange}
          handleAddOwnLinkedinSearch={handleAddOwnLinkedinSearch}
          handleAddOwnLinkedinSearchWithSkipQualification={handleAddOwnLinkedinSearchWithSkipQualification}
          handleJobTitlesChange={handleJobTitlesChange}
          onFileDrop={onFileDrop}
          handleFileChange={handleFileChange}
          exportCampaignContacts={exportCampaignContacts}
          handleChangeFinalContactsPage={handleChangeFinalContactsPage}
          handleFinalContactsSearchChange={handleFinalContactsSearchChange}
          handleClearFinalContactsSearchField={handleClearFinalContactsSearchField}
          handleDeleteContacts={handleDeleteContacts}
          handleAddContactsFromFile={handleAddContactsFromFile}
          handleOpenRenameContactSearchDialog={handleOpenRenameContactSearchDialog}
          handleOpenDeleteContactsSearchDialog={handleOpenDeleteContactsSearchDialog}
          handleOpenDeleteContactsFileDialog={handleOpenDeleteContactsFileDialog}
          handleOpenAddSingleContactDialog={handleOpenAddSingleContactDialog}
          handleAddMultipleContacts={handleAddMultipleContacts}
          handleOpenDeleteContactPermanentlyDialog={handleOpenDeleteContactPermanentlyDialog}
          handleOpenChangeAiJudgementDialog={handleOpenChangeAiJudgementDialog}
          handleOpenLoadContactsNumberDialog={handleOpenLoadContactsNumberDialog}
          handleOpenEditSearchLinkedinAccountDialog={handleOpenEditSearchLinkedinAccountDialog}
          handleBlacklistScopeChange={handleBlacklistScopeChange}
          handleSelectedBlacklistsChange={handleSelectedBlacklistsChange}
          handleOpenAddLocalBlacklistDialog={handleOpenAddLocalBlacklistDialog}
          handleOpenEditBlacklistItemsDialog={handleOpenEditBlacklistItemsDialog}
          handleOpenEditBlacklistNameDialog={handleOpenEditBlacklistNameDialog}
          handleOpenDeleteBlacklistDialog={handleOpenDeleteBlacklistDialog}
          handleRemoveSingleBlacklistItem={handleRemoveSingleBlacklistItem}
          handleSingleContactIsActiveChange={handleSingleContactIsActiveChange}
          handleMultiContactIsActiveChange={handleMultiContactIsActiveChange}
          handleChangeContactsPage={handleChangeContactsPage}
          handleContactsSearchChange={handleContactsSearchChange}
          handleClearContactsSearchField={handleClearContactsSearchField}
          handleOpenContactDrawer={handleOpenContactDrawer}
          handleOpenEnrichContactsDialog={handleOpenEnrichContactsDialog}
          handleSelectNodeFromList={handleSelectNodeFromList}
          handleUnselectNodeFromList={handleUnselectNodeFromList}
          handleOpenFlowCreationDialog={handleOpenFlowCreationDialog}
        handleFlowBuilderApplied={handleFlowBuilderApplied}
          handleOpenChangeCommunicationStyleWarningDialog={handleOpenChangeCommunicationStyleWarningDialog}
          handleIncludeEmojiChange={handleIncludeEmojiChange}
          handleSwissSChange={handleSwissSChange}
          handleLanguageChange={handleLanguageChange}
          handleSecondaryLanguageChange={handleSecondaryLanguageChange}
          onIsActiveChange={onIsActiveChange}
          onGoalChange={onGoalChange}
          onCaseHandlingUpdate={onCaseHandlingUpdate}
          handleUpdateAutoReply={handleUpdateAutoReply}
          handleMessageApprovalChange={handleMessageApprovalChange}
          handleRecontactCooldownChange={handleRecontactCooldownChange}
          isLinkedinAccountMissing={isLinkedinAccountMissing}
          handleSelectedTestContactChange={handleSelectedTestContactChange}
          handleNextContact={handleNextContact}
          handlePrevContact={handlePrevContact}
          handleNextMessage={handleNextMessage}
          handlePrevMessage={handlePrevMessage}
          handleTestCampaign={handleTestCampaign}
          toggleExpanded={toggleExpanded}
          handleUpdateCampaign={handleUpdateCampaign}
          handleOpenStopRunningCampaignDialog={handleOpenStopRunningCampaignDialog}
          contactQualification={contactQualification}
          handleAiQualifierDetailsChange={handleAiQualifierDetailsChange}
          handleAiQualifierPromptChange={handleAiQualifierPromptChange}
          handleAiQualifierSelectedOptionChange={handleAiQualifierSelectedOptionChange}
          handleRejudgeExistingContactsChange={handleRejudgeExistingContactsChange}
          handleIsJudgingChange={handleIsJudgingChange}
          updateQualifyingContactsRecruiting={updateQualifyingContactsRecruiting}
          updateQualifyingContactsLead={updateQualifyingContactsLead}
          handleRemoveContactFromBlacklist={handleRemoveContactFromBlacklist}
          handleRemoveContactFromSuspended={handleRemoveContactFromSuspended}
          handleOpenUpdateSexDialog={handleOpenUpdateSexDialog}
          handleRecoverContactFromDeleted={handleRecoverContactFromDeleted}
          handleContactFilterChange={handleContactFilterChange}
          applySidebarFilter={applySidebarFilter}
          handleActiveQualificationsChange={handleActiveQualificationsChange}
          handleAbortProcessing={handleAbortProcessing}
          toggleDeleteMultipleContacts={toggleDeleteMultipleContacts}
          handleMultipleContactsToDeleteChange={handleMultipleContactsToDeleteChange}
          handleContactsToDeleteChange={handleContactsToDeleteChange}
          handleOpenDeleteMultipleContactsPermanentlyDialog={handleOpenDeleteMultipleContactsPermanentlyDialog}
          handleSelectedLinkedinUserChange={handleSelectedLinkedinUserChange}
        />
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  campaignBuilder: {
    height: 'calc(100vh - 60px - 32px)',
    fontSize: theme.typography.pxToRem(14),
    background: theme.palette.backgrounds.main_new,
    borderRadius: '6px',
    boxShadow: Shadows.GREY_SHADOW_LIGHTER,
    overflow: 'hidden',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  builderBody: {
    flex: 1,
    minHeight: 0,
  },
}));

export default CampaignBuilder;
